import * as math from './energyMath';
import { alignInputs, validSection } from './alignmentDelegate';

function extractSample(row) {
  const payload = row.payload || {};
  const packetTime = payload.timestamp || row.observedAt;
  const sectionTime = section => section && Object.prototype.hasOwnProperty.call(section, 'sampleTime')
    ? section.sampleTime : packetTime;
  if (row.messageType === 'site.telemetry') {
    return { role: 'site', dataStatus: validSection(payload.site) ? undefined : 'unavailable', sampleTime: packetTime, row, values: payload.site?.power || null };
  }
  if (row.messageType === 'bess.telemetry') {
    return { role: 'bess', dataStatus: validSection(payload.bess) && validSection(payload.bess?.pcs) ? undefined : 'unavailable', sampleTime: sectionTime(payload.bess?.pcs), row, values: payload.bess || null };
  }
  if (row.messageType === 'load.telemetry') {
    return { role: 'load', dataStatus: validSection(payload.load) ? undefined : 'unavailable', sampleTime: packetTime, row, values: payload.load || null };
  }
  if (row.messageType === 'pcc.telemetry') {
    return { role: 'pcc', dataStatus: validSection(payload.pcc) ? undefined : 'unavailable', sampleTime: packetTime, row, values: payload.pcc || null };
  }
  if (row.messageType === 'canopy.telemetry') {
    return {
      role: 'canopy', dataStatus: validSection(payload.sabalController?.energy?.inverter) ? undefined : 'unavailable', sampleTime: sectionTime(payload.sabalController?.energy?.inverter),
      row, values: payload.sabalController?.energy || null
    };
  }
  return { role: row.messageType, sampleTime: packetTime, row, values: payload };
}

function calculateSnapshot(rows, explicitBoundary = null, siteConfiguration = null) {
  if (Array.isArray(siteConfiguration)) siteConfiguration = siteConfiguration.length === 1
    ? siteConfiguration[0] : { error: siteConfiguration.length ? 'ambiguousSiteConfiguration' : 'siteConfigurationUnavailable' };
  const samples = rows.map(extractSample);
  const observed = samples.filter(item => ['site', 'bess', 'load', 'pcc', 'canopy'].includes(item.role))
    .map(item => Date.parse(item.sampleTime)).filter(Number.isFinite);
  const boundaryMs = explicitBoundary ? Date.parse(explicitBoundary) : (observed.length ? Math.max(...observed) : Date.now());
  const boundary = new Date(Math.floor(boundaryMs / 10000) * 10000).toISOString();
  const grouped = {};
  for (const sample of samples) (grouped[sample.role] ||= []).push(sample);
  const selectors = siteConfiguration?.authoritativeSources || null;
  const sourceDevices = role => {
    const key = { site: 'generation', bess: 'battery', load: 'servedLoad', pcc: 'pcc' }[role];
    const selector = selectors?.[key];
    return [...new Set((typeof selector === 'string' ? [selector] :
      [...(Array.isArray(selector?.deviceIds) ? selector.deviceIds : []), selector?.deviceId])
      .filter(value => typeof value === 'string' && value))];
  };
  const configured = {};
  for (const role of ['site', 'bess', 'load', 'pcc']) {
    const deviceIds = sourceDevices(role);
    configured[role] = deviceIds.length ?
      (grouped[role] || []).filter(sample => deviceIds.includes(sample.row.deviceId)) : [];
  }
  if (siteConfiguration?.auxiliaryBoundary === 'separately_metered') {
    const supportedRole = siteConfiguration.auxiliarySourceRole === 'servedLoad';
    configured.auxiliary = samples.filter(sample =>
      (!siteConfiguration.auxiliaryDeviceId || sample.row.deviceId === siteConfiguration.auxiliaryDeviceId))
      .map(sample => {
        return supportedRole && typeof sample.values?.servedPowerKw === 'number' ?
          { ...sample, values: { powerKw: sample.values.servedPowerKw } } : null;
      }).filter(Boolean);
  }
  const requiredRoles = ['site', 'bess', 'load', 'pcc',
    ...(siteConfiguration?.auxiliaryBoundary === 'separately_metered' ? ['auxiliary'] : [])];
  const ambiguousRoles = requiredRoles.filter(role =>
    new Set(configured[role].map(sample => sample.row.deviceId)).size > 1);
  const aligned = alignInputs(grouped, boundary, {
    requiredRoles: ['site', 'bess', 'load', 'pcc']
  });
  if (!siteConfiguration || siteConfiguration.error) {
    aligned.alignmentStatus = 'incomplete';
    aligned.reasonCodes.push(siteConfiguration?.error || 'siteConfigurationUnavailable');
  } else {
    const configuredAlignment = alignInputs(configured, boundary, {
      requiredRoles
    });
    Object.assign(aligned, configuredAlignment);
  }
  if (ambiguousRoles.length) {
    aligned.alignmentStatus = 'incomplete';
    aligned.reasonCodes.push(...ambiguousRoles.map(role => `ambiguousSource:${role}`));
  }
  const usable = role => aligned.inputs[role] && !aligned.inputs[role].stale ? aligned.inputs[role].values : null;
  const site = usable('site');
  const bess = usable('bess');
  const load = usable('load');
  const pcc = usable('pcc');
  const auxiliary = usable('auxiliary');
  const balance = math.calculateActivePowerBalance({
    generationKw: site?.generationKw,
    batteryKw: bess?.netPowerKw ?? bess?.actualPowerKw,
    gridKw: pcc?.powerKw,
    servedLoadKw: load?.servedPowerKw,
    // The AC auxiliary boundary requires effective configuration. There is no
    // implicit zero fallback in the read model.
    auxiliaryKw: siteConfiguration?.auxiliaryBoundary === 'included_in_served_load' ||
      siteConfiguration?.auxiliaryBoundary === 'excluded_from_balance' ? 0 : auxiliary?.powerKw,
    alignmentStatus: aligned.alignmentStatus
  });
  const loadConsistency = math.calculateLoadConsistency(
    load?.requestedPowerKw, load?.servedPowerKw, load?.unservedPowerKw
  );
  const feeders = math.calculateFeederMetrics(
    load?.feeders, load?.servedPowerKw, siteConfiguration?.configuredFeederIds || null
  );
  const electrical = math.calculateElectricalCompatibility({
    gridAvailable: pcc?.gridAvailable,
    utilityVoltageV: pcc?.utilitySide?.voltageV,
    siteVoltageV: pcc?.siteSide?.voltageV,
    utilityVoltageBasis: pcc?.utilitySide?.voltageBasis,
    siteVoltageBasis: pcc?.siteSide?.voltageBasis,
    utilityFrequencyHz: pcc?.utilitySide?.frequencyHz,
    siteFrequencyHz: pcc?.siteSide?.frequencyHz
  });
  const bessEnergy = math.calculateBessEnergy({
    directStoredEnergyKwh: bess?.storedEnergyKwh,
    explicitFullChargeCapacityKwh: siteConfiguration?.bessConfiguration?.fullChargeCapacityKwh,
    nameplateCapacityKwh: siteConfiguration?.bessConfiguration?.nameplateCapacityKwh ||
      siteConfiguration?.bessConfiguration?.capacityKwh,
    socPct: bess?.socPct, sohPct: bess?.sohPct,
    minimumSocPct: siteConfiguration?.bessConfiguration?.minimumSocPct ?? bess?.limits?.minimumSocPct,
    maximumSocPct: siteConfiguration?.bessConfiguration?.maximumSocPct ?? bess?.limits?.maximumSocPct,
    dischargeEfficiency: siteConfiguration?.bessConfiguration?.dischargeEfficiency
  });
  const bessEfficiency = math.calculateBessEfficiency({
    chargeEfficiency: siteConfiguration?.bessConfiguration?.chargeEfficiency,
    dischargeEfficiency: siteConfiguration?.bessConfiguration?.dischargeEfficiency
  });
  const bessStoredEnergyBalance = math.calculateBessStoredEnergyBalance({
    chargeEfficiency: siteConfiguration?.bessConfiguration?.chargeEfficiency,
    dischargeEfficiency: siteConfiguration?.bessConfiguration?.dischargeEfficiency,
    internalAuxKwh: siteConfiguration?.bessConfiguration?.internalAuxKwh
  });
  const productionSelector = selectors?.production;
  const productionDeviceIds = [...new Set((typeof productionSelector === 'string' ? [productionSelector] :
    [...(Array.isArray(productionSelector?.deviceIds) ? productionSelector.deviceIds : []),
      productionSelector?.deviceId]).filter(value => typeof value === 'string' && value))];
  const configuredCanopy = productionDeviceIds.length === 1 ?
    (grouped.canopy || []).filter(sample => sample.row.deviceId === productionDeviceIds[0]) : [];
  const canopyAlignment = alignInputs({ canopy: configuredCanopy }, boundary,
    { requiredRoles: ['canopy'] });
  const canopy = canopyAlignment.inputs.canopy && !canopyAlignment.inputs.canopy.stale ?
    canopyAlignment.inputs.canopy.values : null;
  const inverterPerformance = math.calculateInverterPerformance({
    powerKw: typeof canopy?.inverter?.acOutput?.powerW === 'number' ?
      canopy.inverter.acOutput.powerW / 1000 : null,
    ratedPowerKw: siteConfiguration?.authoritativeSources?.production?.ratedPowerKw ??
      siteConfiguration?.bessConfiguration?.inverterRatedPowerKw,
    acOutputEnergyKwh: null, dcInputEnergyKwh: null
  });
  if (productionDeviceIds.length > 1) {
    inverterPerformance.capacityUtilization = math.unavailable('%',
      'ambiguousAuthoritativeProductionSource');
    inverterPerformance.conversionEfficiency = math.unavailable('%',
      'ambiguousAuthoritativeProductionSource');
  } else if (productionDeviceIds.length === 0) {
    inverterPerformance.capacityUtilization = math.unavailable('%',
      'authoritativeProductionSourceUnavailable');
    inverterPerformance.conversionEfficiency = math.unavailable('%',
      'authoritativeProductionSourceUnavailable');
  }
  return {
    boundary, aligned, balance, loadConsistency, feeders, electrical, bessEnergy,
    bessEfficiency, bessStoredEnergyBalance, inverterPerformance
  };
}

export { extractSample, calculateSnapshot };
